import { useState, useEffect } from "react";
import axios from "../http"; // axios instance that attaches the JWT (same one Role.js uses)
import { MASTER_API } from "../apiConfig";

// Must match where user.routes.js is mounted in the backend
// (app.use("/api/<prefix>", ...user.routes)). Check the URL Createuser.js
// uses for ".../list" - the word before "/list" is this value.
const USER_API_PREFIX = "user";

// Resolves the logged-in user's Create/Display/Change/... access (and, for
// Display and Change, per-field visibility/editability) for a given
// module_id (e.g. "SALES_ORDER"), based on the roles created in Create Role.
//
// Admins always get full, unrestricted access - same "user.role === 'admin'"
// check your Sidebar.js already uses for the User Management menu item.
// Masking/restriction only applies to a non-admin user who has actually
// been assigned at least one role (via Assign Role).
//
// MULTIPLE ROLES: a user can hold several roles, and the menu/route
// permissions returned by login are already the union of all of them. This
// hook follows the same rule:
//   * every role is evaluated on its own;
//   * a field is visible/editable if ANY of the user's roles allows it;
//   * a role that has nothing for this module simply contributes nothing.
//
// FIX (user-roles 404): roles are stored per USER ID in user_roles, so the
// hook now calls GET /<USER_API_PREFIX>/:userId/roles (allowed for the user
// themselves or for User Management) instead of the missing /user-roles/:key.
export const useFieldPermissions = (moduleId) => {
  const [loading, setLoading] = useState(true);
  const [hasRole, setHasRole] = useState(false);
  // One array per assigned role, each holding that role's entries for this
  // module_id (one per permission_type, e.g. Display + Change).
  const [roleEntryGroups, setRoleEntryGroups] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      let userId = "";
      let adminFlag = false;
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        adminFlag = user?.role === "admin";
        userId = String(user?.userId || "").trim();
      } catch {
        userId = "";
      }

      if (!cancelled) setIsAdmin(adminFlag);

      // Admin bypasses everything - no need to even fetch roles.
      if (adminFlag) {
        if (!cancelled) {
          setHasRole(false);
          setRoleEntryGroups([]);
          setLoading(false);
        }
        return;
      }

      if (!userId) {
        if (!cancelled) {
          setHasRole(false);
          setRoleEntryGroups([]);
          setLoading(false);
        }
        return;
      }

      // Resolve the user's CURRENT role assignments live from the DB.
      let roleIds = [];
      try {
        const res = await axios.get(
          `${MASTER_API}/${USER_API_PREFIX}/${encodeURIComponent(userId)}/roles`
        );
        // Accept either [{ role_id }] or { roles: [{ role_id }] }
        const list = Array.isArray(res.data) ? res.data : res.data?.roles || [];
        roleIds = list.map((r) => r.role_id ?? r.roleId).filter(Boolean);
      } catch (err) {
        console.error(`useFieldPermissions user-roles lookup error (${userId}):`, err);
      }

      if (roleIds.length === 0) {
        if (!cancelled) {
          setHasRole(false);
          setRoleEntryGroups([]);
          setLoading(false);
        }
        return;
      }

      try {
        // Load every assigned role, not just the first one.
        const responses = await Promise.all(
          roleIds.map((id) => axios.get(`${MASTER_API}/roles/${id}`))
        );

        const groups = responses.map((res) =>
          (res.data?.modules || [])
            .filter((m) => String(m.module_id || "").trim() === moduleId)
            .map((m) => ({
              ...m,
              permission_type: String(m.permission_type || "").trim().toUpperCase()
            }))
        );

        if (!cancelled) {
          setHasRole(true);
          setRoleEntryGroups(groups);
        }
      } catch (err) {
        console.error("useFieldPermissions error:", err);
        if (!cancelled) {
          setHasRole(false);
          setRoleEntryGroups([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  // Every entry from every role, flattened.
  const modulePermEntries = roleEntryGroups.flat();

  // Diagnostic: if a non-admin ends up without approve rights, say exactly
  // what the hook received so the cause is visible in DevTools > Console.
  useEffect(() => {
    if (loading || isAdmin) return;
    const types = modulePermEntries.map((m) => m.permission_type);
    if (
      !types.includes("REVIEW") &&
      !(types.includes("CREATE") && types.includes("CHANGE") && types.includes("DISPLAY"))
    ) {
      console.warn(
        `[useFieldPermissions] ${moduleId}: canApprove=false - hasRole=${hasRole}, ` +
          `roles loaded=${roleEntryGroups.length}, permission types for this module=[${types.join(", ")}]`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAdmin, hasRole, roleEntryGroups, moduleId]);

  // --- Visibility (Display) -------------------------------------------
  //  - no entry for this module            -> false
  //  - has DISPLAY with saved field rows   -> follows the saved is_visible flag
  //  - has DISPLAY with no field rows      -> true
  //  - has only CREATE/CHANGE/REVIEW/CONVERT -> true (no value masking)
  const visibleInRole = (entries, fieldId) => {
    if (entries.length === 0) return false;
    const displayEntry = entries.find((m) => m.permission_type === "DISPLAY");
    if (displayEntry) {
      if (!displayEntry.fields || displayEntry.fields.length === 0) return true;
      return displayEntry.fields.some((f) => f.field_id === fieldId && !!f.is_visible);
    }
    return true;
  };

  const isFieldVisible = (fieldId) => {
    if (isAdmin) return true;
    if (!hasRole) return true;
    if (modulePermEntries.length === 0) return false;
    return roleEntryGroups.some((entries) => visibleInRole(entries, fieldId));
  };

  // --- Editability (Change) -------------------------------------------
  const editableInRole = (entries, fieldId) => {
    if (entries.length === 0) return false;
    const changeEntry = entries.find((m) => m.permission_type === "CHANGE");
    if (changeEntry) {
      if (!changeEntry.fields || changeEntry.fields.length === 0) return true;
      return changeEntry.fields.some((f) => f.field_id === fieldId && !!f.is_visible);
    }
    return true;
  };

  const isFieldEditable = (fieldId) => {
    if (isAdmin) return true;
    if (!hasRole) return true;
    if (modulePermEntries.length === 0) return false;
    return roleEntryGroups.some((entries) => editableInRole(entries, fieldId));
  };

  const hasType = (type) => modulePermEntries.some((m) => m.permission_type === type);

  // "Full access" = ONE role that holds Create + Change + Display (or an
  // explicit FULL/ALL entry) for this module.
  const hasFullAccess = roleEntryGroups.some((entries) => {
    const t = entries.map((m) => m.permission_type);
    return (
      t.includes("FULL") ||
      t.includes("ALL") ||
      (t.includes("CREATE") && t.includes("CHANGE") && t.includes("DISPLAY"))
    );
  });
  const isOnlyType = (type) =>
    !isAdmin &&
    modulePermEntries.length > 0 &&
    modulePermEntries.every((m) => m.permission_type === type);

  return {
    loading,
    isAdmin,
    hasRole,
    permissionType: modulePermEntries[0]?.permission_type || null,
    permissionTypes: modulePermEntries.map((m) => m.permission_type),
    isDisplayOnly: isOnlyType("DISPLAY"),
    isReviewOnly: isOnlyType("REVIEW"),
    isConvertOnly: isOnlyType("CONVERT"),
    hasModuleAccess: isAdmin || !hasRole || modulePermEntries.length > 0,
    canApprove: isAdmin || hasType("REVIEW") || hasFullAccess,
    canConvert: isAdmin || hasType("CONVERT"),
    isFieldVisible,
    isFieldEditable
  };
};